import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";

type Tarifa = {
  id: string;
  nombre: string;
  fecha_inicio: string;
  fecha_fin: string;
  precio_noche: number | null;
  precio_entresemana: number | null;
  prioridad: number;
  precio_individual: number | null;
  precio_individual_entresemana: number | null;
  estancia_minima_noches: number;
};

const empty: Partial<Tarifa> = { nombre: "", fecha_inicio: "", fecha_fin: "", precio_noche: 85, precio_entresemana: 70, prioridad: 0, estancia_minima_noches: 2 };

export const Route = createFileRoute("/_authenticated/gestion/tarifas")({
  component: TarifasPage,
});

function TarifasPage() {
  const [items, setItems] = useState<Tarifa[]>([]);
  const [editing, setEditing] = useState<Partial<Tarifa> | null>(null);
  const [open, setOpen] = useState(false);

  async function load() {
    const { data, error } = await supabase.from("tarifas").select("*").order("fecha_inicio");
    if (error) return toast.error(error.message);
    setItems((data ?? []) as Tarifa[]);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!editing) return;
    const payload = {
      nombre: editing.nombre ?? "",
      fecha_inicio: editing.fecha_inicio ?? "",
      fecha_fin: editing.fecha_fin ?? "",
      multiplicador: 1,
      precio_noche: Number(editing.precio_noche),
      precio_entresemana: editing.precio_entresemana == null || String(editing.precio_entresemana) === "" ? null : Number(editing.precio_entresemana),
      prioridad: Number(editing.prioridad ?? 0),
      precio_individual: editing.precio_individual == null ? null : Number(editing.precio_individual),
      precio_individual_entresemana: editing.precio_individual_entresemana == null ? null : Number(editing.precio_individual_entresemana),
      estancia_minima_noches: Number(editing.estancia_minima_noches),
    };
    const q = editing.id
      ? supabase.from("tarifas").update(payload).eq("id", editing.id)
      : supabase.from("tarifas").insert(payload);
    const { error } = await q;
    if (error) return toast.error(error.message);
    toast.success("Guardada");
    setOpen(false); setEditing(null); load();
  }

  async function remove(id: string) {
    if (!confirm("¿Eliminar esta temporada?")) return;
    const { error } = await supabase.from("tarifas").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  }

  const eur = (n: number | null) => (n == null ? "—" : `${Number(n)} €`);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="font-serif text-xl">Temporadas y precios</h2>
          <p className="text-sm text-muted-foreground">
            Precio por noche de una habitación doble. Viernes y sábado se cobra el precio de fin de semana; el resto de noches, el de entre semana. Fuera de cualquier temporada: 70 € (55 € individual). Cada persona por encima de 2 suma 20 €/noche.
          </p>
          <p className="text-xs text-muted-foreground">Si dos temporadas coinciden (por ejemplo un puente dentro de la temporada baja), gana la de mayor prioridad.</p>
        </div>
        <Button onClick={() => { setEditing({ ...empty }); setOpen(true); }}><Plus className="mr-2 h-4 w-4" />Nueva</Button>
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase text-muted-foreground">
              <tr><th className="p-3">Temporada</th><th>Desde</th><th>Hasta</th><th>Fin de semana</th><th>Entre semana</th><th>Individual (finde / semana)</th><th>Mín. noches</th><th>Prioridad</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className="border-t">
                  <td className="p-3 font-medium">{t.nombre}</td>
                  <td>{t.fecha_inicio}</td>
                  <td>{t.fecha_fin}</td>
                  <td>{eur(t.precio_noche)}</td>
                  <td>{t.precio_entresemana == null ? "Igual" : eur(t.precio_entresemana)}</td>
                  <td>{eur(t.precio_individual)} / {eur(t.precio_individual_entresemana)}</td>
                  <td>{t.estancia_minima_noches}</td>
                  <td>{t.prioridad}</td>
                  <td className="p-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(t); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(t.id)}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Sin temporadas todavía.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif">{editing?.id ? "Editar" : "Nueva"} temporada</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={editing.nombre ?? ""} onChange={(e) => setEditing({ ...editing, nombre: e.target.value })} placeholder="Puente de mayo, Verano…" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Desde</Label><Input type="date" value={editing.fecha_inicio ?? ""} onChange={(e) => setEditing({ ...editing, fecha_inicio: e.target.value })} /></div>
                <div><Label>Hasta</Label><Input type="date" value={editing.fecha_fin ?? ""} onChange={(e) => setEditing({ ...editing, fecha_fin: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Fin de semana (€/noche)</Label><Input type="number" min={0} value={editing.precio_noche ?? ""} onChange={(e) => setEditing({ ...editing, precio_noche: Number(e.target.value) })} /></div>
                <div><Label>Entre semana (€/noche)</Label><Input type="number" min={0} value={editing.precio_entresemana ?? ""} placeholder="Igual" onChange={(e) => setEditing({ ...editing, precio_entresemana: e.target.value === "" ? null : Number(e.target.value) })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Individual finde (€)</Label><Input type="number" min={0} value={editing.precio_individual ?? ""} onChange={(e) => setEditing({ ...editing, precio_individual: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                <div><Label>Individual entre semana (€)</Label><Input type="number" min={0} value={editing.precio_individual_entresemana ?? ""} onChange={(e) => setEditing({ ...editing, precio_individual_entresemana: e.target.value === "" ? null : Number(e.target.value) })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Mín. noches</Label><Input type="number" min={1} value={editing.estancia_minima_noches ?? 1} onChange={(e) => setEditing({ ...editing, estancia_minima_noches: Number(e.target.value) })} /></div>
                <div><Label>Prioridad</Label><Input type="number" value={editing.prioridad ?? 0} onChange={(e) => setEditing({ ...editing, prioridad: Number(e.target.value) })} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
